/**
 * Named gesture-tuning presets.
 *
 * Centralising the magic numbers keeps the feel consistent across gestures and
 * makes tuning a one-line change. Each preset bundles the recognizer's
 * `fling`/`thresholdPx` with the consumer-facing layout constants (commit
 * thresholds, animation durations) that the recognizer itself does not own.
 */

import type { FlingConfig } from "./types";

const GESTURE_DEADZONE_PX = 8;

const STANDARD_FLING: FlingConfig = {
  maxDurationMs: 300,
  minDistancePx: 50,
  axisRatio: 2,
};

/** Reader: horizontal swipe to page between chapters (carousel slide). */
export const READER_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  /** Commit at the smaller of a fixed travel or a fraction of the panel width. */
  commitThresholdPx: 64,
  commitThresholdRatio: 0.2,
  transitionMs: 240,
  fallbackWidthPx: 360,
  hapticMs: 12,
  fling: { ...STANDARD_FLING },
} as const;

/** Reader: vertical pull-down from the top of the reader to open the
 *  book/chapter/verse selector. Engages only at scrollTop 0 (see `canEngage`).
 *  Rubber-band keeps the pull feeling tethered before it commits. */
export const READER_PULL_NAV = {
  thresholdPx: GESTURE_DEADZONE_PX,
  /** Travel (px) past which the release opens the selector. */
  commitThresholdPx: 96,
  /** Linear up to this travel, damped beyond (rubber-band). */
  rubberBandLimitPx: 120,
  rubberBandFactor: 0.45,
  /** Snap-back / settle animation. */
  transitionMs: 240,
  hapticMs: 12,
  fling: { ...STANDARD_FLING, minDistancePx: 60 },
} as const;

/** Toast notifications: horizontal swipe to dismiss the individual toast.
 *  Tracks the finger 1:1 along x, fades as it travels, and either flings off
 *  (fling or past the commit threshold) or snaps back. */
export const TOAST_SWIPE_DISMISS = {
  thresholdPx: GESTURE_DEADZONE_PX,
  /** Travel (px) past which the release dismisses the toast. */
  commitThresholdPx: 80,
  /** Off-screen travel used for the fling-out animation. */
  flingOutPx: 480,
  transitionMs: 220,
  hapticMs: 8,
  fling: { ...STANDARD_FLING, minDistancePx: 40 },
} as const;

/** Bottom-sheet panels (e.g. Strong concordance on mobile): vertical drag-down
 *  to dismiss. Mirrors the Dialog drag-to-dismiss feel for non-Dialog sheets. */
export const SHEET_PULL_DISMISS = {
  thresholdPx: GESTURE_DEADZONE_PX,
  /** Travel (px) past which the release closes the sheet. */
  commitThresholdPx: 100,
  /** Damping applied to an upward over-pull (the sheet should barely move up). */
  upwardFactor: 0.12,
  transitionMs: 300,
  hapticMs: 10,
  fling: { ...STANDARD_FLING, minDistancePx: 60 },
} as const;
